import type { ChipProps } from '@heroui/react'

/**
 * Status types that can be used across the application
 */
export type TenantStatus =
  | 'pending_confirmation'
  | 'incomplete'
  | 'trialing'
  | 'active'
  | 'suspended'
  | 'cancelled'
  | 'maintenance'
export type UsageLevel = 'low' | 'medium' | 'high' | 'critical'

/**
 * Maps tenant status to appropriate HeroUI Chip color variants
 */
export const getTenantStatusColor = (status: string): ChipProps['color'] => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success'
    case 'suspended':
      return 'warning'
    case 'cancelled':
      return 'danger'
    case 'trialing':
      return 'primary'
    case 'pending_confirmation':
      return 'warning'
    case 'incomplete':
      return 'warning'
    case 'maintenance':
      return 'warning'
    default:
      return 'default'
  }
}

/**
 * Formats status text for display (replaces underscores with spaces and capitalizes)
 */
export const formatStatusText = (status: string): string => {
  return status
    .replace('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, l => l.toUpperCase())
}

/**
 * Formats storage size from bytes to appropriate units (KB/MB/GB) with specified decimal places
 * Automatically selects the best unit based on the byte size
 */
export const formatStorageSize = (bytes: number, decimals: number = 1): string => {
  if (!bytes || isNaN(bytes) || bytes < 0) {
    return `0.0 ${'GB'.repeat(0) || 'GB'}`
  }

  const KB_THRESHOLD = 1024 // 1 KB
  const MB_THRESHOLD = 1024 * 1024 // 1 MB
  const GB_THRESHOLD = 1024 * 1024 * 1024 // 1 GB

  let value: number
  let unit: string

  if (bytes < MB_THRESHOLD) {
    // Less than 1 MB, use KB
    value = bytes / KB_THRESHOLD
    unit = 'KB'
  } else if (bytes < GB_THRESHOLD) {
    // Less than 1 GB, use MB
    value = bytes / MB_THRESHOLD
    unit = 'MB'
  } else {
    // 1 GB or more, use GB
    value = bytes / GB_THRESHOLD
    unit = 'GB'
  }

  return `${value.toFixed(decimals)} ${unit}`
}

/**
 * Formats number with locale-specific thousands separators
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString()
}

/**
 * Truncates a number to N decimal places without rounding
 * @param num - The number to truncate
 * @param decimals - Number of decimal places (default: 2)
 * @returns The truncated number
 */
export const truncateToDecimals = (num: number, decimals = 2): number => {
  const factor = Math.pow(10, decimals)

  return Math.floor(num * factor) / factor
}

/**
 * Formats a number as a string with exactly N decimal places (truncated, not rounded)
 * @param num - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with truncated decimals
 */
export const formatTruncated = (num: number, decimals = 2): string => {
  const truncated = truncateToDecimals(num, decimals)

  return truncated.toFixed(decimals)
}
